import { useEffect, useMemo, useState } from 'react';
import {
  PiArrowClockwiseDuotone,
  PiCopyDuotone,
  PiLinkBreak,
  PiSlidersDuotone,
  PiTrashDuotone,
} from 'react-icons/pi';
import { Box3, Vector2, Vector3 } from 'three';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useDollhouse, useSelection } from '@spazious/dollhouse-engine';
import {
  boxFromNode,
  FCompositionNode,
  FGroupNode,
  FNode,
  FNodeTypesEnum,
  getNodeFromObject,
  getWorldPosition,
} from '@spazious/dollhouse-fsetup';
import { Translate } from '@spazious/dollhouse-i18n';
import { join } from '@spazious/dollhouse-selector';
import { useGraph, useStaging, useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum } from '@spazious/dollhouse-types';
import {
  Button,
  ButtonGroup,
  ButtonGroupProps,
  ButtonProps,
  Flex,
  Tooltip,
  Typography,
} from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { ROUTE_ABS_PATH_INSPECTOR } from '../../constants/index.js';
import {
  useCommander,
  useDelayedSelection,
  useDeviceInfo,
  useUnlinkNode,
} from '../../hooks/index.js';
import { isEditableTarget, moveNodesInNavMesh } from '../../utils/index.js';
import { ConfirmDialog } from '../ConfirmDialog/index.js';
import { Features } from '../Features/index.js';
import { DeleteNodesConfirmationForm } from '../InspectorPanel/DeleteNodesConfirmationForm.js';
import { UnlinkNodesConfirmationForm } from '../InspectorPanel/UnlinkNodesConfirmationForm.js';
import { MenuNavButton } from '../MenuNavButton/index.js';
import { RotationInput } from '../RotationInput/index.js';
import { ToggleButtonLabel } from '../ToggleButtonLabel/index.js';

const _box = new Box3();
const _size = new Vector3();

const DUPLICATION_OFFSET = 1; // meters. Position offset of duplicated furniture from original

const duplicationEnabledNodes = [
  FNodeTypesEnum.Furniture,
  FNodeTypesEnum.Decoration,
  FNodeTypesEnum.FComposition,
  FNodeTypesEnum.FGroup,
];

const rotationEnabledNodes = [
  FNodeTypesEnum.Furniture,
  FNodeTypesEnum.Decoration,
  FNodeTypesEnum.FComposition,
  FNodeTypesEnum.FGroup,
];

const isNestedFurniture = (node: FNode): boolean => {
  return node.type === FNodeTypesEnum.Furniture && !!node.originator;
};

export interface UserMenuProps extends ButtonGroupProps {}

export const UserMenu: React.FC<UserMenuProps> = (props) => {
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [unlinkConfirmDialogOpen, setUnlinkConfirmDialogOpen] = useState(false);
  const [rotateActive, setRotateActive] = useState(false);
  const { selected, select } = useSelection();
  const { staging } = useStaging();
  const commander = useCommander();
  const { set } = useStore();
  const askBeforeDeleteNodes = useStore(({ settings }) => settings.askBeforeDeleteNodes);
  const askBeforeUnlinkNodes = useStore(({ settings }) => settings.askBeforeUnlinkNodes);
  const showButtonTitles = useStore(({ settings }) => settings.showButtonTitles);
  const lookMode = useStore(({ controls }) => controls === ControlsModeEnum.Walk);
  const isMobile = useDeviceInfo((state) => state.isMobile);
  const buttonSize: ButtonProps['size'] = isMobile ? 'small' : 'medium';

  const navMeshQuery = useDollhouse(({ world }) => world?.navMeshQuery);

  const { setSelectors, resetSelection } = useDelayedSelection();
  const { unlink } = useUnlinkNode();

  // TODO this is provisional "Subscribes to the graph"
  useGraph();

  const nodes = useMemo(
    () =>
      selected.reduce((acc, object) => {
        const graphNode = getNodeFromObject(object) as FNode | null;

        return graphNode ? [...acc, graphNode] : acc;
      }, [] as FNode[]),
    [selected]
  );

  /**
   * List of
   * - Independednt FNode that are not part of a composition
   * - FComposition nodes where all its furnitures are selected
   *
   * - Empty if any composistion is partially selected
   */
  const completeNodes = useMemo((): FNode[] => {
    const targetNodes = new Set<FNode>();

    const instanced = new Map<FCompositionNode, Set<FNode>>();
    nodes.forEach((node) => {
      if (node.type === FNodeTypesEnum.Furniture && node.originator) {
        const key = node.originator;
        const value = instanced.get(key) ?? new Set<FNode>();

        value.add(node);
        instanced.set(key, value);
      } else {
        targetNodes.add(node);
      }
    });

    for (let [originator, nodes] of instanced.entries()) {
      const fnodes = new Set();
      originator.traverse((node) => {
        if (node.type === FNodeTypesEnum.Furniture && node.visible !== false) {
          fnodes.add(node);
        }
      });

      if (fnodes.size === nodes.size) {
        targetNodes.add(originator);
      } else {
        // incomplete compostion selected
        targetNodes.clear();
        break;
      }
    }

    return Array.from(targetNodes);
  }, [nodes]);

  const canDelete: boolean = useMemo(() => {
    return !lookMode && completeNodes.length > 0;
  }, [lookMode, completeNodes]);

  const canDuplicate: boolean = useMemo(() => {
    return (
      !lookMode &&
      completeNodes.length > 0 &&
      completeNodes.every((node) => duplicationEnabledNodes.includes(node.type))
    );
  }, [lookMode, completeNodes]);

  const canRotate: boolean = useMemo(() => {
    return (
      !lookMode &&
      completeNodes.length > 0 &&
      completeNodes.every((node) => rotationEnabledNodes.includes(node.type))
    );
  }, [lookMode, completeNodes]);

  const canUnlink: boolean = useMemo(() => {
    return (
      nodes.length > 0 &&
      nodes.every((node) => node.type === FNodeTypesEnum.FComposition || isNestedFurniture(node))
    );
  }, [nodes]);

  const doUnlink = useEventCallback(() => {
    const compositions = new Set<FCompositionNode>(); // full compositions to be exploded

    nodes.forEach((node) => {
      if (
        node.type === FNodeTypesEnum.Furniture &&
        node.originator?.type === FNodeTypesEnum.FComposition
      ) {
        compositions.add(node.originator);
      } else if (node.type === FNodeTypesEnum.FComposition) {
        compositions.add(node);
      }
    });
    select([]);
    unlink(Array.from(compositions));
  });

  const handleUnlinkBtnClick = useEventCallback(() => {
    if (askBeforeUnlinkNodes) {
      setUnlinkConfirmDialogOpen(true);
      return;
    }
    doUnlink();
  });

  const doDelete = useEventCallback(() => {
    const action = commander.createAction(
      CommanderActionTypesEnum.Delete,
      join(completeNodes.map((node) => node.selector!))
    );

    action.commit();
    select([]);
  });

  const handleDeleteConfirmClose = useEventCallback(() => {
    setDeleteConfirmDialogOpen(false);
  });

  const handleDeleteBtnClick = useEventCallback(() => {
    if (askBeforeDeleteNodes) {
      setDeleteConfirmDialogOpen(true);
      return;
    }
    doDelete();
  });

  // Delete / Backspace removes the current selection, mirroring the delete
  // button (and its "ask before deleting" confirmation). Ignored while typing.
  useEffect(() => {
    const keydown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') {
        return;
      }
      if (!canDelete || isEditableTarget(e.target)) {
        return;
      }
      e.preventDefault();
      handleDeleteBtnClick();
    };

    window.addEventListener('keydown', keydown);

    return () => window.removeEventListener('keydown', keydown);
  }, [canDelete, handleDeleteBtnClick]);

  const duplicateSelectedNodes = useEventCallback(() => {
    staging.prepare();

    const targetNodes: Exclude<FNode, FGroupNode>[] = completeNodes.filter(
      (node) => node.type !== FNodeTypesEnum.FGroup
    ) as Exclude<FNode, FGroupNode>[];

    const movingNodes: {
      center: Vector2;
      size: Vector2;
      theta: number;
    }[] = targetNodes.map((node) => {
      boxFromNode(node, _box);
      _box.getSize(_size);
      return {
        center: new Vector2().fromArray(getWorldPosition(node)),
        size: new Vector2(_size.x, _size.z),
        theta: node.rotation,
      };
    });

    // try movement in south-east direction
    const reqMovement: Vector3 = movingNodes.reduce((acc, node) => {
      acc.x = Math.min(acc.x, node.size.x / 2);
      acc.z = Math.min(acc.z, node.size.y / 2);
      return acc;
    }, new Vector3(DUPLICATION_OFFSET, 0, DUPLICATION_OFFSET));

    let { success, selectedMovement: movement } = moveNodesInNavMesh(
      movingNodes,
      reqMovement,
      navMeshQuery!
    );

    // if cannot be moved in south-east, try north-west
    if (!success || (Math.abs(movement.x) < 0.001 && Math.abs(movement.z) < 0.001)) {
      reqMovement.x *= -1;
      reqMovement.z *= -1;
      movement = moveNodesInNavMesh(movingNodes, reqMovement, navMeshQuery!).selectedMovement;
    }

    resetSelection();

    const action = commander.createAction(
      CommanderActionTypesEnum.Translate,
      join(targetNodes.map((node) => node.selector)),
      {
        movement: [movement.x, movement.z],
        copy: true,
      }
    );

    action.commit();

    setSelectors(action.affectedNodes().map(({ selector }) => selector));
  });

  const allFurnitureNodes = useMemo(
    () => (nodes.length ? nodes.every((nodes) => nodes.type === FNodeTypesEnum.Furniture) : false),
    [nodes]
  );

  const isFcompositionNode = useMemo(
    () => nodes.length === 1 && nodes[0].type === FNodeTypesEnum.FComposition,
    [nodes]
  );

  useEffect(() => {
    nodes && setRotateActive(false);
  }, [nodes]);

  useEffect(() => {
    set({ showNavMesh: rotateActive });
  }, [set, rotateActive]);

  return (
    <ButtonGroup {...props}>
      <Features features="graphs.setup.node:editable">
        <Tooltip
          children={({ open }) => {
            if (!open && rotateActive) {
              setRotateActive(false);
            }
            return (
              <ToggleButtonLabel
                actived={rotateActive}
                disabled={!canRotate}
                label={
                  <Typography size="small" variant="label">
                    <Translate>Rotate</Translate>
                  </Typography>
                }
                size={buttonSize}
                onClick={() => setRotateActive(!rotateActive)}
              >
                <PiArrowClockwiseDuotone />
              </ToggleButtonLabel>
            );
          }}
          content={
            <Flex padding="calc(var(--spacing-padding)*2)">
              <RotationInput />
            </Flex>
          }
          placement="bottom"
          trigger="click"
        />
      </Features>
      <Features features="graphs.setup.node:editable">
        <Button
          disabled={!canDelete}
          hotkey="Delete"
          size={buttonSize}
          onClick={handleDeleteBtnClick}
        >
          <PiTrashDuotone />
          {showButtonTitles && (
            <Typography size="small" variant="label">
              <Translate>Delete</Translate>
            </Typography>
          )}
        </Button>
      </Features>
      <Features features="graphs.setup.node:editable">
        <Button disabled={!canDuplicate} size={buttonSize} onClick={duplicateSelectedNodes}>
          <PiCopyDuotone />
          {showButtonTitles && (
            <Typography size="small" variant="label">
              <Translate>Duplicate</Translate>
            </Typography>
          )}
        </Button>
      </Features>
      <Features features="graphs.setup.node:editable">
        <Button disabled={!canUnlink} size={buttonSize} onClick={handleUnlinkBtnClick}>
          <PiLinkBreak />
          {showButtonTitles && (
            <Typography size="small" variant="label">
              <Translate>Unlink</Translate>
            </Typography>
          )}
        </Button>
      </Features>
      <MenuNavButton
        disabled={!nodes.length || !(allFurnitureNodes || isFcompositionNode)}
        size={buttonSize}
        to={ROUTE_ABS_PATH_INSPECTOR}
      >
        <PiSlidersDuotone />
        {showButtonTitles && (
          <Typography size="small" variant="label">
            <Translate>Edit</Translate>
          </Typography>
        )}
      </MenuNavButton>
      <ConfirmDialog
        fullWidth
        confirmButtonLabel="Remove"
        content={<DeleteNodesConfirmationForm nodes={nodes} />}
        maxWidth="600px"
        open={deleteConfirmDialogOpen}
        setting="askBeforeDeleteNodes"
        title={<Translate>Remove object?</Translate>}
        onClose={handleDeleteConfirmClose}
        onConfirm={doDelete}
      />
      <ConfirmDialog
        fullWidth
        content={<UnlinkNodesConfirmationForm nodes={nodes} />}
        maxWidth="600px"
        open={unlinkConfirmDialogOpen}
        setting="askBeforeUnlinkNodes"
        onClose={() => setUnlinkConfirmDialogOpen(false)}
        onConfirm={doUnlink}
      />
    </ButtonGroup>
  );
};
